import { ApolloError } from '@apollo/client';

import {
  STRIPE_ERROR_CODE_CARD_DECLINED,
  STRIPE_ERROR_TYPE_CARD_ERROR,
} from 'global/constants';

type NetworkErrorResult = {
  result?: { errors?: Array<{ message?: unknown }> };
};

const getServerErrorObject = (error?: ApolloError) => {
  const networkMessage = (error?.networkError as unknown as NetworkErrorResult)
    ?.result?.errors?.[0]?.message;
  const graphQLMessage = error?.graphQLErrors?.[0]?.message;
  const fullMessage =
    typeof networkMessage === 'string'
      ? networkMessage
      : typeof graphQLMessage === 'string'
        ? graphQLMessage
        : undefined;

  let type: string | undefined;
  let code: string | undefined;
  let message: string | undefined;
  if (fullMessage?.includes('|')) {
    [type, code, message] = fullMessage.split('|');
  } else {
    message = fullMessage;
  }

  return { type, code, message };
};

export const getServerErrorMessage = (
  error?: ApolloError
): string | null | undefined => getServerErrorObject(error).message;

export const getServerErrorReason = (
  error?: ApolloError
): string | null | undefined =>
  (error?.networkError as any)?.result?.reason?.[0]?.message;

/**
 * The server answered and refused — a 4xx, or any GraphQL error. Retrying an
 * identical request reproduces it, unlike a transport failure (no `statusCode`)
 * or a 5xx. Both branches are needed because Apollo converts every response with
 * status >= 300 into a `networkError`, so a resolver's 403 never arrives as a
 * `graphQLErrors` entry.
 */
export const isPermanentApolloError = (error?: ApolloError): boolean => {
  if (error?.graphQLErrors?.length) return true;
  const statusCode = (
    error?.networkError as { statusCode?: number } | null | undefined
  )?.statusCode;
  return !!statusCode && statusCode >= 400 && statusCode < 500;
};

export const isCardDeclinedError = (error?: ApolloError) => {
  const { type, code } = getServerErrorObject(error);
  return (
    type === STRIPE_ERROR_TYPE_CARD_ERROR ||
    code === STRIPE_ERROR_CODE_CARD_DECLINED
  );
};
